import { styled, type Theme } from "@mui/material/styles";
import {
  Box,
  CircularProgress,
  Tooltip,
  Typography,
  circularProgressClasses,
  type TooltipProps
} from "@mui/material";

type SreamStateSpinnerProps = {
  streamHasError: boolean;
  showSpinner: boolean;
  errorCaption?: string | null;
  size?: number;
  tooltipPlacement?: TooltipProps["placement"];
  progress?: number;
  position?: number;
  loopCount?: number;
};

const StyledStreamSpinner = styled(CircularProgress)(({ theme }) => ({
  color: theme.palette.primary.main,
  [`& .${circularProgressClasses.circle}`]: {
    strokeLinecap: "round"
  }
}));

const StyledStreamSpinnerTrack = styled(CircularProgress)(({ theme }) => ({
  color: theme.palette.divider,
  [`& .${circularProgressClasses.circle}`]: {
    strokeLinecap: "round"
  }
}));

const formatTimestamp = (seconds: number): string => {
  if (!Number.isFinite(seconds)) {
    return "—";
  }
  const totalMs = Math.max(0, Math.floor(seconds * 1000));
  const hours = Math.floor(totalMs / 3_600_000);
  const minutes = Math.floor((totalMs % 3_600_000) / 60_000);
  const secs = Math.floor((totalMs % 60_000) / 1000);
  const ms = totalMs % 1000;

  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(secs).padStart(2, "0")}.${String(ms).padStart(3, "0")}`;
};

const SreamStateSpinner = ({
  streamHasError,
  showSpinner,
  errorCaption = null,
  size = 12,
  tooltipPlacement,
  progress,
  position,
  loopCount
}: SreamStateSpinnerProps) => {
  const hasProgress = typeof progress === "number" && Number.isFinite(progress);
  const hasPosition = typeof position === "number" && Number.isFinite(position);
  const hasLoopCount = typeof loopCount === "number" && Number.isFinite(loopCount);
  const progressValue = hasProgress ? Math.min(100, Math.max(0, progress)) : undefined;
  const spinnerTooltip = streamHasError
    ? errorCaption ?? "Error: Stream is in error state"
    : showSpinner
      ? "Stream is running"
      : "Stream is stopped";
  const spinnerVariant = hasProgress ? "determinate" : showSpinner ? "indeterminate" : "determinate";
  const spinnerThickness = showSpinner ? 4 : 1;
  const spinnerSx = streamHasError
    ? { color: "error.main" }
    : showSpinner
      ? {}
      : {
          color: "text.secondary",
          [`& .${circularProgressClasses.circle}`]: {
            stroke: (theme: Theme) => theme.palette.text.disabled
          }
        };
  const tooltipTitle = hasProgress || hasPosition || hasLoopCount ? (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
      <Typography variant="caption">{spinnerTooltip}</Typography>
      {hasProgress ? (
        <Typography variant="caption">Progress: {progressValue!.toFixed(2)}%</Typography>
      ) : null}
      {hasPosition ? (
        <Typography variant="caption">Position: {formatTimestamp(position!)}</Typography>
      ) : null}
      {hasLoopCount ? (
        <Typography variant="caption">LoopCount: {Math.floor(loopCount!)}</Typography>
      ) : null}
    </Box>
  ) : spinnerTooltip;

  return (
    <Tooltip title={tooltipTitle} placement={tooltipPlacement}>
      <Box sx={{ position: "relative", width: size, height: size, display: "inline-flex" }}>
        <StyledStreamSpinnerTrack
          size={size}
          thickness={spinnerThickness}
          variant="determinate"
          value={100}
          sx={{ position: "absolute", inset: 0 }}
        />
        <StyledStreamSpinner
          size={size}
          thickness={spinnerThickness}
          variant={spinnerVariant}
          {...(hasProgress ? { value: progressValue } : !showSpinner ? { value: 100 } : {})}
          sx={{ ...spinnerSx, position: "absolute", inset: 0 }}
        />
      </Box>
    </Tooltip>
  );
};

export default SreamStateSpinner;
